import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

// Actions
import {
  downloadTemplate,
  resetSiteUploadState,
  uploadTemplate,
} from "../../../actions/SiteUploadActions";
import { getCampaignOverview } from "../../../actions/campaign-report/CampaignReportActions";

// Utils and Constants
import { selectFile } from "../../../common-utils/file-utils/FileUtils";
import { DispatchFrom } from "../../../constants/GeneralConstants";
import { checkIsCoralMediaSpecific } from "../../../common-utils/user-utils/userUtils";

// Components
import DownloadTemplateCard from "../../../components/site-upload-utils/DownloadTemplateCard";
import UploadTemplateCard from "../../../components/site-upload-utils/UploadTemplateCard";
import { CloseSiteUpload } from "../../../components/site-upload-utils/Utils";
import GenerateTemplateCard from "../../../components/site-upload-utils/GenerateTemplateCard";
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import TargetGroupSelectionCard from "../../../components/site-upload-utils/TargetGroupSelectionCard";
import CampaignTypeSelectionCard from "../../../components/site-upload-utils/CampaignTypeSelectionCard";
import CampaignTargetGroupForm from "../../../components/campaign-tg-form/CampaignTargetGroupForm";
import Spinner from "../../../components/spinner/Spinner";

// Page Functions
function getMavinSiteUploadCardsData(isCoralMediaSpecific) {
  // steps number
  const uploadSiteListStepNo = isCoralMediaSpecific ? 4 : 3;
  const generateTemplateStepNo = isCoralMediaSpecific ? 5 : 4;

  // data
  const cardsData = {
    DownloadTemplateData: {
      step: "STEP 1",
      title: "Download and fill template",
      message: "If you already have a filled site template jump to next step.",
      buttonLabel: "Download Template",
    },
    TargetGroupData: {
      step: "STEP 2",
      title: "Select target group",
      message: "Please select a target group for a campaign.",
    },
    CampaignTypeData: {
      step: "STEP 3",
      title: "Select campaign type",
      message: "Please select a campaign type for a campaign.",
    },
    UploadSiteListData: {
      step: `STEP ${uploadSiteListStepNo}`,
      title: "Upload images and site list",
      message:
        "Please upload using the template format recommended to avoid errors.",
      buttonLabel: "Upload Template",
    },
    GenerateTemplateData: {
      step: `STEP ${generateTemplateStepNo}`,
      title: "Generate site report",
      message:
        "We automatically create a report for you that can be modified as needed.",
      buttonLabel: "Generate Report",
    },
  };

  return cardsData;
}

function PageHeaderSection({ campaignId, cityId }) {
  const pageFrom = DispatchFrom.campaignAnalyser;

  // variables
  const pageTitle = "Upload sites via excel";
  const pageActions = (
    <CloseSiteUpload
      campaignId={campaignId}
      cityId={cityId}
      pageFrom={pageFrom}
    />
  );

  return <PageHeader title={pageTitle} actions={pageActions} shadow={false} />;
}

// get Final TgAttr And PoiTypeIds
function getFinalTgAttrAndPoiTypeIds(
  campaignId,
  campaign,
  isTargetGroupEnabled = false,
  selectedTgAttrs = [],
  selectedPoiTypes = []
) {
  // for existing campaigns
  if (campaignId && campaign) {
    const { tgAttributesIds = [], poiTypeIds = [] } = campaign || {};
    return {
      finalTgAttributesIds: tgAttributesIds,
      finalPoiTypeIds: poiTypeIds,
    };
  }

  // for new campaigns with selection
  if (isTargetGroupEnabled) {
    return {
      finalTgAttributesIds: selectedTgAttrs,
      finalPoiTypeIds: selectedPoiTypes,
    };
  }

  // for new campaigns without selection
  return { finalTgAttributesIds: [], finalPoiTypeIds: [] };
}

/**
 * Page
 */
function CampaignCreatePage() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { state = {} } = location || {};
  const {
    campaignId = "",
    cityId = "",
    // targetGroupId = "",
    exCampaignType,
  } = state || {};

  // State
  const [campaignType, setCampaignType] = useState("");

  // Tg Form Related State
  const [isTargetGroupEnabled, setIsTargetGroupEnabled] = useState(false);
  const [selectedTgAttrs, setSelectedTgAttrs] = useState([]);
  const [selectedPoiTypes, setSelectedPoiTypes] = useState([]);

  // from uploaded-template
  const campaignData =
    useSelector((state) => state.c_siteUpload.campaignData) || {};
  const mediaCount = useSelector((state) => state.c_siteUpload.mediaCount);
  const templateDownloading = useSelector(
    (state) => state.c_siteUpload.downloadLoading
  );

  // campaign overview
  const campaignSummaryLoading = useSelector(
    (state) => state.campaignReport.campaignSummaryLoading
  );
  const { campaign = {} } = useSelector(
    (state) => state.campaignReport.campaignSummary
  );

  // campaign overview
  useEffect(() => {
    if (campaignId) {
      dispatch(getCampaignOverview(campaignId));
    }
  }, [dispatch, campaignId]);

  // update campaignType with existing campaignType
  useEffect(() => {
    if (exCampaignType) {
      setCampaignType(exCampaignType);
    }
  }, [exCampaignType]);

  // downloaded template fileName and link to Redirect on Summary-page
  const fileName = "Site_Upload_Template.xlsx";
  const linkTo = `/campaign/${
    campaignId ? campaignId : campaignData.id
  }/summary`;

  // object for reducerKey and selectorValues to pass in "uploadTemplate"
  const reducerKeyValuesObj = {
    reducerKey: "c_siteUpload",
    selectorValue: {
      loading: "uploadLoading",
      error: "uploadError",
      mediaCount: "mediaCount",
    },
  };

  useEffect(() => {
    dispatch(resetSiteUploadState());
  }, [dispatch]);

  // this function handles file-uploads
  async function uploadTemplateFunction(imageFiles) {
    const files = await selectFile();

    // final tgAttr and poiTypes
    const { finalTgAttributesIds, finalPoiTypeIds } =
      getFinalTgAttrAndPoiTypeIds(
        campaignId,
        campaign,
        isTargetGroupEnabled,
        selectedTgAttrs,
        selectedPoiTypes
      );

    dispatch(
      uploadTemplate(
        campaignId,
        files,
        false,
        "",
        imageFiles,
        campaignType,
        finalTgAttributesIds,
        finalPoiTypeIds
      )
    );
  }

  // coral media
  const isCoralMediaSpecific = checkIsCoralMediaSpecific();

  // cards data
  const mavinSiteUpload = getMavinSiteUploadCardsData(isCoralMediaSpecific);

  // Checks for page loading
  if (campaignSummaryLoading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  return (
    <div className="content-wrapper bg-alt h-100 site-upload">
      <PageHeaderSection campaignId={campaignId} cityId={cityId} />

      <div className="page-content">
        <div className="row">
          <div className={isTargetGroupEnabled ? "col-6" : "col-12"}>
            {/* for downloading template of excel file */}
            <DownloadTemplateCard
              isDisable={templateDownloading}
              onClick={() => dispatch(downloadTemplate(fileName))}
              downloadTemplateData={mavinSiteUpload.DownloadTemplateData}
            />

            {/* for selecting target group */}
            <TargetGroupSelectionCard
              targetGroupData={mavinSiteUpload.TargetGroupData}
              isTargetGroupEnabled={isTargetGroupEnabled}
              setIsTargetGroupEnabled={setIsTargetGroupEnabled}
              campaignId={campaignId}
            />

            {/* CoralMedia :: Campaign Type */}
            {isCoralMediaSpecific && (
              <CampaignTypeSelectionCard
                campaignTypeData={mavinSiteUpload.CampaignTypeData}
                campaignType={campaignType}
                setCampaignType={setCampaignType}
                campaignId={campaignId}
              />
            )}

            {/* for excel-file template upload */}
            <UploadTemplateCard
              uploadSiteListData={mavinSiteUpload.UploadSiteListData}
              reducerKeyValuesObj={reducerKeyValuesObj}
              uploadTemplateFunction={uploadTemplateFunction}
            />

            {/* to redirect to campaign-summary-page */}
            <GenerateTemplateCard
              linkTo={linkTo}
              mediaCount={mediaCount}
              generateTemplateData={mavinSiteUpload.GenerateTemplateData}
              campaignId={campaignId}
            />
          </div>

          {/* Campaign TG Form */}
          {isTargetGroupEnabled && (
            <CampaignTargetGroupForm
              className="mt-3"
              selectedTgAttrs={selectedTgAttrs}
              setSelectedTgAttrs={setSelectedTgAttrs}
              selectedPoiTypes={selectedPoiTypes}
              setSelectedPoiTypes={setSelectedPoiTypes}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default CampaignCreatePage;
